/* ============================================================================
 * Movatic WMS — Frontend | CesarFilho 932048016
 * ProductImagesDialog — gestao de imagens de um produto (ADR-0042 catalogo/anti-fraude).
 * Mirror do IssueAttachmentsCard (GET lista + POST multipart + invalidate), dentro do
 * <Dialog> caseiro. MVP so'-upload (o backend nao tem DELETE/PATCH). Teto de 4 (E4).
 * ========================================================================== */
import React, { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Image as ImageIcon, Star, X } from 'lucide-react'
import api from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

const MAX = 4

type ProductImage = {
  id: number
  image: string // URL relativa (/media/...)
  alt_text?: string
  is_cover?: boolean
  order_no?: number
}
type ListResponse = { results?: ProductImage[] } | ProductImage[]

function asArray(data: ListResponse | undefined): ProductImage[] {
  if (!data) return []
  return Array.isArray(data)
    ? data
    : Array.isArray(data.results)
      ? data.results
      : []
}

/** Miniatura com fallback de icone SEM loop: ao falhar, troca para estado (nao outra <img>). */
function Thumb({ src, cover }: { src: string; cover?: boolean }) {
  const [broken, setBroken] = useState(false)
  return (
    <div className="relative h-20 w-20 overflow-hidden rounded border">
      {broken || !src ? (
        <div className="flex h-full w-full items-center justify-center bg-black/5">
          <ImageIcon className="h-6 w-6 opacity-40" />
        </div>
      ) : (
        <img
          src={src}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      )}
      {cover && (
        <Star className="absolute right-0.5 top-0.5 h-4 w-4 fill-amber-400 text-amber-400" />
      )}
    </div>
  )
}

type Props = {
  product: { id: number; code: string; name1?: string }
  open: boolean
  onClose: () => void
}

export default function ProductImagesDialog({ product, open, onClose }: Props) {
  const qc = useQueryClient()
  const [file, setFile] = useState<File | null>(null)
  const [isCover, setIsCover] = useState(false)

  const q = useQuery({
    queryKey: ['product-images', product.id],
    queryFn: async () => {
      const { data } = await api.get<ListResponse>(
        `/product/products/${product.id}/images/`,
      )
      return asArray(data)
    },
    enabled: open,
  })

  const images = q.data ?? []
  const atLimit = images.length >= MAX

  const m = useMutation({
    mutationFn: async () => {
      const fd = new FormData()
      fd.append('image', file as File)
      if (isCover) fd.append('is_cover', 'true')
      const { data } = await api.post(
        `/product/products/${product.id}/images/`,
        fd,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
      return data
    },
    onSuccess: () => {
      setFile(null)
      setIsCover(false)
      qc.invalidateQueries({ queryKey: ['product-images', product.id] })
    },
  })

  return (
    <Dialog open={open}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>
              Imagens — {product.code}
              {product.name1 ? ` · ${product.name1}` : ''}
            </DialogTitle>
            <button
              className="btn subtle"
              onClick={onClose}
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </DialogHeader>

        <div className="mb-1 text-xs opacity-60">
          {images.length} de {MAX}
        </div>

        {/* Grelha de miniaturas */}
        {q.isLoading ? (
          <div className="text-sm opacity-60">A carregar imagens…</div>
        ) : images.length === 0 ? (
          <div className="rounded-xl border border-dashed p-6 text-center text-sm opacity-60">
            Sem imagens. Carrega a primeira abaixo.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {images.map((img) => (
              <Thumb key={img.id} src={img.image} cover={img.is_cover} />
            ))}
          </div>
        )}

        {/* Bloco de upload */}
        <div className="mt-4 border-t pt-4">
          {atLimit ? (
            <div className="rounded bg-amber-500/15 px-3 py-2 text-sm text-amber-600">
              Limite de {MAX} atingido.
            </div>
          ) : (
            <div className="grid gap-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                disabled={m.isPending}
                className="text-sm"
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={isCover}
                  onChange={(e) => setIsCover(e.target.checked)}
                  disabled={m.isPending}
                />
                Definir como capa
              </label>
              <div>
                <button
                  className="btn"
                  onClick={() => m.mutate()}
                  disabled={!file || m.isPending}
                >
                  {m.isPending ? 'A carregar…' : 'Carregar'}
                </button>
              </div>
              {m.isError && (
                <div className="text-xs text-red-600">
                  Falha ao carregar. Tenta de novo.
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
